import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Modal } from "./Modal";
import type { Ingredient, Store, StoreSection } from "../types";

export const COMMON_UNITS = ["g", "kg", "ml", "l", "ks", "balení", "lžíce", "lžička"];

/**
 * Sdílený modální formulář pro vytvoření / úpravu suroviny.
 *
 * `ingredient === null` → režim CREATE (po uložení zavolá `onCreated(id)` s novým id).
 * `ingredient` zadané → režim UPDATE (po uložení jen invaliduje cache).
 * Po úspěchu vždy invaliduje `["ingredients"]` query, takže všechny seznamy
 * v aplikaci (Suroviny, RecipeDetailPage, Lednice…) se přerefetchnou.
 */
export function IngredientForm({
  ingredient,
  stores,
  sectionsByStore,
  onClose,
  onCreated,
}: {
  ingredient: Ingredient | null;
  stores: Store[];
  sectionsByStore: Map<string, StoreSection[]>;
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(ingredient?.name ?? "");
  const [unit, setUnit] = useState(ingredient?.unit ?? "g");
  // byWeight = surovina na váhu (uloží se s packageSize=0); jinak uživatel zadává konkrétní velikost balení.
  const [byWeight, setByWeight] = useState(ingredient?.packageSize === 0);
  const [packageSize, setPackageSize] = useState<number | "">(
    ingredient && ingredient.packageSize > 0 ? ingredient.packageSize : ""
  );
  const [mappings, setMappings] = useState<Record<string, string>>(
    () => Object.fromEntries((ingredient?.storeSections ?? []).map((m) => [m.storeId, m.sectionId]))
  );

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const payload = {
        name: name.trim(),
        unit: unit.trim(),
        packageSize: byWeight ? 0 : Number(packageSize),
        storeSections: Object.entries(mappings)
          .filter(([, sid]) => !!sid)
          .map(([storeId, sectionId]) => ({ storeId, sectionId })),
      };
      if (ingredient) {
        await api.patch(`/api/ingredients/${ingredient.id}`, payload);
        return ingredient.id;
      }
      const res = await api.post<{ id: string }>("/api/ingredients", payload);
      return res.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["ingredients"] });
      if (!ingredient && onCreated) onCreated(id);
      onClose();
    },
  });

  const canSave =
    !!name.trim() && !!unit.trim() && (byWeight || (!!packageSize && Number(packageSize) > 0));

  return (
    <Modal
      open
      onClose={onClose}
      title={ingredient ? `Upravit "${ingredient.name}"` : "Nová surovina"}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Zrušit
          </button>
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
          >
            <span aria-hidden>💾</span>
            <span>Uložit</span>
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">Název</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus={!ingredient}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Jednotka</label>
            <input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} list="ingredient-form-units" />
            <datalist id="ingredient-form-units">
              {COMMON_UNITS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="label">Velikost balení ({unit || "?"})</label>
            <input
              className="input disabled:bg-slate-100 disabled:text-slate-400"
              type="number"
              step="0.01"
              min={0}
              value={byWeight ? "" : packageSize}
              disabled={byWeight}
              placeholder={byWeight ? "—" : undefined}
              onChange={(e) => setPackageSize(e.target.value === "" ? "" : Number(e.target.value))}
            />
            <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={byWeight}
                onChange={(e) => setByWeight(e.target.checked)}
              />
              <span>Na váhu (prodává se volně)</span>
            </label>
            <p className="text-xs text-slate-500 mt-1">
              {byWeight
                ? "V nákupním seznamu se zobrazí přímo potřebné množství (nepřepočítává se na balení)."
                : "Nákupní seznam zaokrouhlí potřebné množství na celá balení nahoru."}
            </p>
          </div>
        </div>

        <div>
          <h3 className="label mb-2">Sekce v obchodech</h3>
          {stores.length === 0 ? (
            <p className="text-xs text-slate-500">
              Nejprve si vytvořte obchody a jejich sekce. Sekci můžete přiřadit i později v Katalogu surovin.
            </p>
          ) : (
            <div className="space-y-2">
              {stores.map((s) => {
                const sections = sectionsByStore.get(s.id) ?? [];
                return (
                  <div key={s.id} className="flex items-center gap-2">
                    <div className="flex-1 text-sm">{s.name}</div>
                    <select
                      className="input max-w-xs"
                      value={mappings[s.id] ?? ""}
                      onChange={(e) =>
                        setMappings((m) => {
                          const next = { ...m };
                          if (e.target.value) next[s.id] = e.target.value;
                          else delete next[s.id];
                          return next;
                        })
                      }
                    >
                      <option value="">— bez sekce —</option>
                      {sections.map((sec) => (
                        <option key={sec.id} value={sec.id}>
                          {sec.name}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
